import { Trash } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { Button } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { useAgGridTableContext, useNotificationContext } from 'core/states';
import { useQuotePartActions } from 'quotes/hooks';
import { IBaseQuote, IQuotePart } from 'quotes/types';
import { Color, Variant } from 'theme/enums';

import { RemoveConfirmation } from './RemoveConfirmation';

interface IProps {
  quote: IBaseQuote;
}

export const RemoveButton = ({ quote }: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { showSuccessNotification, isLoadingTopNotificationDisplayed } = useNotificationContext();
  const { selectedRows, gridApi, deselectAllRows } = useAgGridTableContext();

  const { handleCliJob } = useCliJobHandler();
  const { removeQuoteParts } = useQuotePartActions({
    notificationPosition: NotificationPosition.TOP,
  });

  const onRemove = async () => {
    setIsConfirmationOpen(false);

    const removedQuotePartsIds = selectedRows?.map((part: IQuotePart) => part.id);

    handleCliJob(
      removeQuoteParts,
      {
        ids: removedQuotePartsIds,
        quoteId: quote.id,
      },
      {
        cliJobInProgressMessage: t('messageLinesRemoveInProgress', {
          count: selectedRows.length,
        }),
        onCliJobFinished: () => {
          showSuccessNotification(
            { message: t('messageLinesRemoveSucces', { count: selectedRows.length }) },
            NotificationPosition.TOP
          );

          deselectAllRows();

          gridApi?.setGridOption('loading', true);
          queryClient.invalidateQueries('vendorQuoteParts');
          // Removing a line can flip trade_agreement_status Sent -> Updated Post Sent server-side.
          queryClient.invalidateQueries(['quote']);
        },
      }
    );
  };

  return (
    <>
      <Button
        text={t('buttonLabelRemove')}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<Trash />}
        onClick={() => setIsConfirmationOpen(true)}
        disabled={isLoadingTopNotificationDisplayed}
      />

      <RemoveConfirmation
        isOpen={isConfirmationOpen}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
